import React, { useEffect, useRef, useState, useMemo } from "react";

export default function SelectScenarios({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [dropdownUp, setDropdownUp] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);

  const [filter, setFilter] = useState("");

  const formatLabel = (input) => {
    return String(input ?? "").trim().replace(/\s+/g, " ");
  };

  const normalized = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options
      .filter(Boolean)
      .map((opt) => {
        if (typeof opt === "string" || typeof opt === "number") {
          return { value: String(opt), label: formatLabel(opt) };
        }
        return {
          value: String(opt.value ?? opt.label ?? ""),
          label: formatLabel(opt.label ?? opt.value ?? ""),
        };
      })
      .filter((o) => o.value !== undefined && o.value !== null);
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (!filter.trim()) return normalized;
    const term = filter.trim().toLowerCase();
    return normalized.filter((opt) =>
      opt.label.toLowerCase().includes(term),
    );
  }, [normalized, filter]);

  const selected = normalized.find(
    (o) => String(o.value) === String(value)
  );
  const selectedLabel = selected?.label || "";

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const availableBottom = window.innerHeight - rect.bottom;
    const dropdownHeight = 240; // max-h-60 ~ 240px

    // Determinar si el dropdown debe estar arriba o abajo
    if (availableBottom < dropdownHeight && rect.top > dropdownHeight) {
      setDropdownUp(true);
    } else {
      setDropdownUp(false);
    }
  }, [open]);

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, []);

  const handlePick = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  return (
    <div className={`relative w-full ${className}`} ref={ref}>
      {label && (
        <label className="text-[12px] text-[#686873] block pb-1">
          {label}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => {
            const next = !prev;
            if (next) setFilter("");
            return next;
          });
        }}
        className={`
          w-full relative
          rounded-xl border border-slate-200 bg-slate-50
          px-3 py-3 pr-10
          text-left text-[14px] text-slate-900
          shadow-sm outline-none
          ${disabled ? "opacity-60 cursor-not-allowed" : "hover:border-slate-300"}
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedLabel ? "" : "text-[#A7A7A7]"}>
          {selectedLabel || placeholder}
        </span>

        <span
          className={`
            pointer-events-none
            absolute right-3 top-1/2 -translate-y-1/2
            w-2 h-2 border border-[#686873] border-t-0 border-l-0 rotate-45
            transition-transform
            ${open ? "rotate-[225deg] translate-y-[-20%]" : ""}
          `}
        />
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: dropdownUp ? "auto" : "100%",
            bottom: dropdownUp ? "100%" : "auto",
            left: 0,
            right: 0,
          }}
          className="bg-white border border-[#F2F2F2] rounded-lg shadow-lg z-[9999] max-h-68 overflow-hidden mt-1"
        >
          <div className="p-3">
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          <div className="max-h-52 overflow-y-auto">
            {filteredOptions.length === 0 && (
              <div className="px-4 py-2 text-[14px] text-[#98999A]">
                No options available
              </div>
            )}

            {filteredOptions.map((opt) => {
              const isSelected =
                String(value) === String(opt.value);

              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handlePick(opt.value)}
                  className={`
                    w-full flex items-center justify-between
                    px-4 py-2 text-[15px]
                    ${
                      isSelected
                        ? "bg-[#F0F5F0] text-[#0F0F0F]"
                        : "text-[#101010]"
                    }
                    hover:bg-[#F7F9FA]
                  `}
                >
                  <span className="text-left">
                    {opt.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}